/* eslint-disable no-param-reassign */
import { message } from 'antd';
import axios from 'axios';
import { dateFormatterToReporteador } from 'utils/formatValues';
// import { logger } from 'utils/debug';

const {
  REACT_APP_REPORTEADOR_SCHEME: SCHEME,
  REACT_APP_REPORTEADOR_APIHOST: APIHOST,
  REACT_APP_REPORTEADOR_PATH: PATH,
} = process.env;

const PROD = process.env.REACT_APP_ENV === 'PRODUCTION';

export const baseURL = `${SCHEME}://${APIHOST}${PATH}`;

const defaultOptions = {
  baseURL,
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
};

const http = axios.create(defaultOptions);

const requestInterceptor = async (options) => {
  if (options.url[options.url.length - 1] !== '/') {
    options.url = `${options.url}/`;
  }
  return options;
};

http.interceptors.request.use(requestInterceptor);

const logger = (error) => {
  // eslint-disable-next-line no-console
  console.error(error);
  const data = error?.response?.data;
  const wide = { width: '70%', marginLeft: '15%' };
  if (data?.errors?.['$.ID']?.[0]) {
    return message.warn(data.errors['$.ID'][0], 10);
  }
  if (data?.MensajeError) {
    return message.warn({ content: data.MensajeError, style: wide }, 10);
  }
  /*
  Un 400 de validación llega como { errors: { '<campo>': ['<detalle>'] } } con llaves
  que varían según el campo rechazado, por eso se toma el primer detalle disponible
  en lugar de buscar una llave fija.
  */
  const validationError = data?.errors && Object.entries(data.errors)
    .map(([campo, detalles]) => `${campo}: ${[].concat(detalles).join(' ')}`)
    .join(' | ');
  if (validationError) {
    return message.warn({ content: validationError, style: wide }, 10);
  }
  if (data?.title) {
    return message.warn({ content: data.title, style: wide }, 10);
  }
  return message.warn(error.message, 10);
};

/**
 * POST para obtener base64 del reporteador
 * @param {Object} values
 * @param {'pdf'|'excel'|'docx'|'csv'} type
 * @returns {Promise<string>}
 */
export const postObtenerReporte = async (values, type = 'pdf') => {
  try {
    const { data } = await http.post(`api/ConsumirReportes/ObtenerReporte_${type}`, values);
    return `data:application/pdf;base64,${data.Documento.Base64}`;
  } catch (err) {
    logger(err);
    return null;
  }
};

/**
 * POST para obtener base64 del reporteador
 * @param {Object} values
 * @returns {Promise<string>}
 */
export const postObtenerDocumento = async (values, type = 'pdf') => {
  try {
    const { data } = await http.post(`api/ConsumirReportes/ObtenerReporte_${type}`, values);
    const { Error: hasError, MensajeError } = data;
    if (hasError) {
      logger(MensajeError);
      return [MensajeError, null];
    }
    const datatype = type === 'pdf' ? 'pdf' : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const base64 = `data:application/${datatype};base64,${data.Documento.Base64}`;
    return [null, base64];
  } catch (err) {
    logger(err);
    // El servicio detalla el motivo en MensajeError incluso cuando responde con 4xx
    return [err?.response?.data?.MensajeError || 'Ha ocurrido un error al obtener el reporte'];
  }
};

/**
 * @description GET PDF Declaracion CRIE
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getDeclaracionCRIEPDF = async (id) => {
  try {
    const values = {
      ID: PROD ? 167 : 169,
      Parametros: [
        {
          Nombre: 'id_declaracion',
          TipoParametro: 'Entero',
          Valor: id,
        },
      ],
    };
    const { data } = await http.post('api/ConsumirReportes/ObtenerReporte_pdf', values);
    const { Error: hasError, MensajeError } = data;
    if (hasError) {
      logger(MensajeError);
      return [MensajeError, null];
    }
    const base64 = `data:application/pdf;base64,${data.Documento.Base64}`;
    return [null, base64];
  } catch (error) {
    logger(error);
    return ['Ha ocurrido un error al obtener el reporte'];
  }
};

/**
 * @description GET PDF Declaracion NOTARIOS
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getDeclaracionNOTARIOPDF = async (id) => {
  try {
    const values = {
      ID: PROD ? 166 : 171,
      Parametros: [
        {
          Nombre: 'declaracionID',
          TipoParametro: 'Entero',
          Valor: id,
        },
      ],
    };
    const { data } = await http.post('api/ConsumirReportes/ObtenerReporte_pdf', values);
    const { Error: hasError, MensajeError } = data;
    if (hasError) {
      logger(MensajeError);
      return [MensajeError, null];
    }
    const base64 = `data:application/pdf;base64,${data.Documento.Base64}`;
    return [null, base64];
  } catch (error) {
    logger(error);
    return ['Ha ocurrido un error al obtener el reporte'];
  }
};

/**
 * @description GET PDF Declaracion ALCOHOLESS
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getDeclaracionAlcoholesPDF = async (id) => {
  try {
    const values = {
      ID: PROD ? 189 : 193,
      Parametros: [
        {
          Nombre: 'declaracionID',
          TipoParametro: 'Entero',
          Valor: id,
        },
      ],
    };
    const { data } = await http.post('api/ConsumirReportes/ObtenerReporte_pdf', values);
    const { Error: hasError, MensajeError } = data;
    if (hasError) {
      logger(MensajeError);
      return [MensajeError, null];
    }
    const base64 = `data:application/pdf;base64,${data.Documento.Base64}`;
    return [null, base64];
  } catch (error) {
    logger(error);
    return ['Ha ocurrido un error al obtener el reporte'];
  }
};

/**
 * @description GET PDF Declaracion HOSPEDAJE
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getDeclaracionHospedajePDF = async (id) => {
  try {
    const values = {
      ID: PROD ? 198 : 195,
      Parametros: [
        {
          Nombre: 'declaracionID',
          TipoParametro: 'Entero',
          Valor: id,
        },
      ],
    };
    const { data } = await http.post('api/ConsumirReportes/ObtenerReporte_pdf', values);
    const { Error: hasError, MensajeError } = data;
    if (hasError) {
      logger(MensajeError);
      return [MensajeError, null];
    }
    const base64 = `data:application/pdf;base64,${data.Documento.Base64}`;
    return [null, base64];
  } catch (error) {
    logger(error);
    return ['Ha ocurrido un error al obtener el reporte'];
  }
};

/**
 * @description GET PDF Declaracion HOSPEDAJE
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getDeclaracionNominasPDF = async (id) => {
  try {
    const values = {
      ID: PROD ? 199 : 200,
      Parametros: [
        {
          Nombre: 'declaracionID',
          TipoParametro: 'Entero',
          Valor: id,
        },
      ],
    };
    const { data } = await http.post('api/ConsumirReportes/ObtenerReporte_pdf', values);
    const { Error: hasError, MensajeError } = data;
    if (hasError) {
      logger(MensajeError);
      return [MensajeError, null];
    }
    const base64 = `data:application/pdf;base64,${data.Documento.Base64}`;
    return [null, base64];
  } catch (error) {
    logger(error);
    return ['Ha ocurrido un error al obtener el reporte'];
  }
};

export const REPORTES_BITACORA = {
  APLICAR_DESCUENTO: 132,
  CARGO_PRESCRIPTO: 131,
  CANCELAR_CARGO: null,
  CARGO_EXENTO: null,
};

export const REPORTE_DOCUMENTO_CONVENIO = 134;

/**
 * @description ID del reporte de bitácora correspondiente a una acción
 * @param {string} accion - clave de la acción (ej. 'APLICAR_DESCUENTO')
 * @returns {number|null}
 */
export const getReporteBitacoraID = (accion) => REPORTES_BITACORA[accion] ?? null;

/**
 * @description Reporte de la bitácora de movimientos para el rango de fechas indicado.
 * La acción seleccionada determina cuál reporte se solicita; el reporte en sí solo
 * recibe el rango de fechas como parámetros. Se pide aparte de la tabla porque la
 * exportación en pantalla solo alcanza los registros ya cargados, no el total.
 * @param {Object} params
 * @param {string|Object} params.fecha_inicio - string o instancia de Moment
 * @param {string|Object} params.fecha_fin - string o instancia de Moment
 * @param {string} params.accion - clave de la acción (ej. 'APLICAR_DESCUENTO')
 * @param {'pdf'|'excel'} type
 * @returns {Promise<[string|null, string|null]>} [error, base64]
 */
export const getReporteBitacora = async ({ fecha_inicio, fecha_fin, accion }, type = 'pdf') => {
  const ID = getReporteBitacoraID(accion);
  if (!ID) {
    return ['El reporte para la acción seleccionada aún no está disponible', null];
  }
  const values = {
    ID,
    Parametros: [
      {
        Nombre: 'fechainicio',
        TipoParametro: 'Fecha',
        Valor: dateFormatterToReporteador(fecha_inicio),
      },
      {
        Nombre: 'fechafinal',
        TipoParametro: 'Fecha',
        Valor: dateFormatterToReporteador(fecha_fin, false),
      },
    ],
  };
  return postObtenerDocumento(values, type);
};

export default http;
